import { ExpandableConfig } from 'antd/lib/table/interface'
import { useState, useEffect } from 'react'
import { ExpandableSection } from './components/ExpandableSection'
import { UseExpandableSectionOptions } from './types'

/**
 * wraps up logic needed to resolve the expandable section for CIMS Tables
 *
 * @see {@link ExpandableSection}
 */
export function useExpandableSection<T extends object>({
  dataSource,
  rowKey,
  expand,
}: UseExpandableSectionOptions<T>) {
  const [showCollapseButton, setShowCollapseButton] = useState(false)
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([])
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Stop-gap for existing behavior.
    setShowCollapseButton(dataSource.length > 1 && expandedRowKeys.length > 0)
  }, [expandedRowKeys, dataSource])
  const [expandable, setExpandable] = useState<ExpandableConfig<T>>({})
  useEffect(() => {
    const defaults: ExpandableConfig<T> = {
      expandRowByClick: true,
      expandedRowKeys,
      onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Stop-gap for existing behavior.
    if (!expand) return setExpandable(defaults)
    const { expandable, render } = expand
    return setExpandable({
      ...defaults,
      expandedRowRender: (record, ...rest) => {
        const keyResult = typeof rowKey === 'function' ? rowKey(record) : rowKey
        return (
          <ExpandableSection
            key={`expandable-${keyResult}`}
            record={record}
            {...render(record, ...rest)}
          />
        )
      },
      rowExpandable:
        typeof expandable === 'function'
          ? expandable
          : () => expandable ?? true,
      expandedRowClassName: () => 'cims-table-expanded',
    })
  }, [expand, expandedRowKeys, rowKey])
  return { showCollapseButton, expandable, setExpandedRowKeys }
}
